/**
 * Mutu Buah PDF Generation Utility
 * Generates formatted PDF reports for fruit quality inspection data
 * Using small font sizes to fit A4 paper
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { id, ru } from 'date-fns/locale';
import { MutuBuahData, MutuBuahGroupedData, PenaltyRate } from '../types';
import { trackDownload } from '../../../activity';

/**
 * Group data by mandor
 */
export const groupDataByMandor = (data: MutuBuahData[]): MutuBuahGroupedData[] => {
    const grouped = new Map<string, MutuBuahGroupedData>();

    data.forEach(item => {
        if (!grouped.has(item.supervisor)) {
            grouped.set(item.supervisor, {
                supervisor: item.supervisor,
                job_name: item.job_name,
                job_level: item.job_level,
                mandor: item.mandor,
                kerani: item.kerani,
                estate: item.estate,
                divisi: item.divisi,
                asisten: item.asisten,
                manager: item.manager,
                askep: item.askep,
                items: [],
            });
        }
        grouped.get(item.supervisor)!.items.push(item);
    });
    return Array.from(grouped.values());
};

/**
 * Calculate subtotals for a group of items by kerani
 */
const calculateSubtotals = (items: MutuBuahData[]) => {
    const subtotals = {
        total_qty: 0,
        ripe_qty: 0,
        unripe_qty: 0,
        rotten_qty: 0,
        lstalk_qty: 0,
        loose_qty: 0,
        abnormal_01_qty: 0,
        abnormal_02_qty: 0,
        abnormal_03_qty: 0,
        abnormal_04_qty: 0,
        total_rupiah: 0,
    };

    items.forEach(item => {
        subtotals.total_qty += item.total_qty || 0;
        subtotals.ripe_qty += item.ripe_qty || 0;
        subtotals.unripe_qty += item.unripe_qty || 0;
        subtotals.rotten_qty += item.rotten_qty || 0;
        subtotals.lstalk_qty += item.lstalk_qty || 0;
        subtotals.loose_qty += item.loose_qty || 0;
        subtotals.abnormal_01_qty += item.abnormal_01_qty || 0;
        subtotals.abnormal_02_qty += item.abnormal_02_qty || 0;
        subtotals.abnormal_03_qty += item.abnormal_03_qty || 0;
        subtotals.abnormal_04_qty += item.abnormal_04_qty || 0;
        subtotals.total_rupiah += item.total_rupiah || 0;
    });

    return subtotals;
};
const formatNumber = (num: number) => {
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(num);
};

const formatDecimal = (num?: number | null) => {
    if (!num) return '';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2
    }).format(num);
};



/**
 * Group items by kerani for subtotals
 */
const groupByKerani = (items: MutuBuahData[]): Map<string, MutuBuahData[]> => {
    const grouped = new Map<string, MutuBuahData[]>();

    items.forEach(item => {
        if (!grouped.has(item.kerani)) {
            grouped.set(item.kerani, []);
        }
        grouped.get(item.kerani)!.push(item);
    });

    return grouped;
};



/**
 * Calculate percentage
 */
const calculatePercentage = (value: number, total: number): string => {
    if (total === 0) return '0%';
    return ((value / total) * 100).toFixed(2) + '%';
};

/**
 * Clean name - remove / NIP suffix if present
 */
const cleanName = (name: string): string => {
    if (!name) return '';
    return name.split('/')[0].trim();
};

/**
 * Clean name - remove / NIP suffix if present
 */
const cleanSupervisorName = (supervisor: string): string => {
    if (!supervisor) return '';
    return supervisor.split('/')[0].trim();
};

/**
 * Generate PDF for mutu buah inspection
 */
export const generateMutuBuahPDF = (
    groupedData: MutuBuahGroupedData[],
    inspectionDate: string,
    penaltyRates?: PenaltyRate[]
) => {
    // A4 Landscape orientation for better table width
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margins = { top: 8, bottom: 20, left: 5, right: 5 };

    // Calculate table widths to match HTML layout
    // Column widths (in mm): Mandor(16) + Kerani(32) + Blok(12) + TPH(11) + Potong(12) + Pemanen(16) + Total(12) + MSK(10) + MTH(10) + BST(10) + BTP(10) + BRD(10) + BDT(10) + BKL(10) + PTY(10) + BBT(10)
    const tableWidth = 16 + 32 + 12 + 11 + 12 + 16 + 12 + 10 + 10 + 10 + 10 + 10 + 10 + 10 + 10 + 10; // 201mm
    const tableMargin = (pageWidth - tableWidth) / 2; // Center position

    // Process each mandor group (each on a new page)
    groupedData.forEach((group, groupIndex) => {
        if (groupIndex > 0) {
            doc.addPage();
        }

        let currentY = margins.top;

        // Add logos to header (disabled per request)
        // try {
        //     // Left logo aligned to table's left edge
        //     const leftLogoWidth = 20; const leftLogoHeight = 11;
        //     const leftLogoX = margins.left;
        //     doc.addImage('/GBS-Logo-Green-Yellow.png', 'PNG', leftLogoX, currentY, leftLogoWidth, leftLogoHeight);
        //
        //     // Right logo aligned to table's right edge
        //     const rightLogoWidth = 18; const rightLogoHeight = 15;
        //     const rightLogoRightEdge = pageWidth - margins.right;
        //     const rightLogoX = rightLogoRightEdge - rightLogoWidth;
        //     doc.addImage('/bombagrup.png', 'PNG', rightLogoX, currentY, rightLogoWidth, rightLogoHeight);
        // } catch (error) {
        //     console.warn('Could not load logos:', error);
        // }

        // Title
        currentY = 15;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('INSPEKSI PANEN (MUTU BUAH)', pageWidth / 2, currentY, { align: 'center' });

        // Shared bounds aligned with logos (full width between page margins)
        const leftLogoX = margins.left; // left edge of left logo
        const rightLogoRight = pageWidth - margins.right; // right edge of right logo
        const sharedLeftMargin = leftLogoX;
        const sharedRightMargin = pageWidth - rightLogoRight;
        const sharedWidth = rightLogoRight - leftLogoX;

        // Header information - only Estate & Divisi
        currentY = 25;
        doc.setFontSize(8); // increased for better readability
        doc.setFont('helvetica', 'normal');

        // Align with table left margin
        const col1X = sharedLeftMargin;
        const col2X = pageWidth / 2;
        const valueX1 = col1X + 22;
        const valueX2 = col2X + 22;
        const lineHeight = 4;

        // Left column only
        doc.text('Estate (kebun)', col1X, currentY);
        doc.text(`: ${group.estate}`, valueX1, currentY);

        doc.text('Divisi', col1X, currentY + lineHeight);
        doc.text(`: ${group.divisi}`, valueX1, currentY + lineHeight);

        doc.text(group.job_name, col1X, currentY + (lineHeight * 2));
        doc.text(`: ${cleanSupervisorName(group.supervisor)}`, valueX1, currentY + (lineHeight * 2));

        doc.text('Tanggal Inspeksi', col2X, currentY);
        doc.text(`: ${format(new Date(inspectionDate), 'dd/MM/yyyy', { locale: id })}`, valueX2, currentY);


        currentY += (lineHeight * 2) + 2;

        // Prepare table data with proper structure
        const tableData: any[] = [];
        let isFirstRow = true; // show mandor only once at the very first data row

        // Group by kerani for subtotals
        const keraniGroups = groupByKerani(group.items);

        const keraniArray = Array.from(keraniGroups.entries());
        keraniArray.forEach((entry) => {
            const [kerani, items] = entry;

            // Subtotal per unique block inside this kerani group
            const blocks = new Map<string, MutuBuahData[]>();
            items.forEach(it => {
                const key = it.block || '-';
                if (!blocks.has(key)) blocks.set(key, []);
                blocks.get(key)!.push(it);
            });

            Array.from(blocks.entries()).forEach(([blockKey, blockItems]) => {
                blockItems.forEach((item, itemIdx) => {
                    tableData.push({
                        mandor: isFirstRow ? (group.mandor || '') : '', // only show Mandor once at first row
                        kerani: itemIdx === 0 ? (kerani || '') : '',
                        block: item.block || '',
                        tph: item.tph || '',
                        potong: item.fp_id || '',
                        pemanen: item.pemanen || '',
                        total_qty: item.total_qty || '',
                        msk: item.ripe_qty || '',
                        mth: item.unripe_qty || '',
                        bst: item.rotten_qty || '',
                        btp: item.lstalk_qty || '',
                        brd: item.loose_qty ? formatDecimal(item.loose_qty) : '',
                        bdt: item.abnormal_01_qty || '',
                        bkl: item.abnormal_02_qty || '',
                        pty: item.abnormal_03_qty || '',
                        bbt: item.abnormal_04_qty || '',
                        rupiah: formatNumber(item.total_rupiah || 0),
                        isSubtotal: false,
                        isTotal: false,
                    });
                    isFirstRow = false; // after first row, set to false
                });

                // Add subtotal row per block
                const subtotals = calculateSubtotals(blockItems);
                tableData.push({
                    mandor: '',
                    kerani: 'Sub Total',
                    block: '',
                    tph: '',
                    potong: '',
                    pemanen: '',
                    total_qty: subtotals.total_qty || '',
                    msk: subtotals.ripe_qty || '',
                    mth: subtotals.unripe_qty || '',
                    bst: subtotals.rotten_qty || '',
                    btp: subtotals.lstalk_qty || '',
                    brd: subtotals.loose_qty ? formatDecimal(subtotals.loose_qty) : '',
                    bdt: subtotals.abnormal_01_qty || '',
                    bkl: subtotals.abnormal_02_qty || '',
                    pty: subtotals.abnormal_03_qty || '',
                    bbt: subtotals.abnormal_04_qty || '',
                    rupiah: formatNumber(subtotals.total_rupiah || 0),
                    isSubtotal: true,
                    isTotal: false,
                });
            });
        });

        // Add total row
        const grandTotal = calculateSubtotals(group.items);
        tableData.push({
            mandor: '',
            kerani: 'Total',
            block: '',
            tph: '',
            potong: '',
            pemanen: '',
            total_qty: grandTotal.total_qty || '',
            msk: grandTotal.ripe_qty || '',
            mth: grandTotal.unripe_qty || '',
            bst: grandTotal.rotten_qty || '',
            btp: grandTotal.lstalk_qty || '',
            brd: grandTotal.loose_qty ? formatDecimal(grandTotal.loose_qty) : '',
            bdt: grandTotal.abnormal_01_qty || '',
            bkl: grandTotal.abnormal_02_qty || '',
            pty: grandTotal.abnormal_03_qty || '',
            bbt: grandTotal.abnormal_04_qty || '',
            rupiah: formatNumber(grandTotal.total_rupiah || 0),
        });

        // Convert to array format for autoTable
        const bodyData = tableData.map(row => [
            row.mandor,
            row.kerani,
            row.block,
            row.tph,
            row.potong,
            row.pemanen,
            row.total_qty,
            row.msk,
            row.mth,
            row.bst,
            row.btp,
            row.brd,
            row.bdt,
            row.bkl,
            row.pty,
            row.bbt,
            row.rupiah,
        ]);

        // Compute column widths to fit exactly between logos
        const wMandor = 24; // widened to accommodate NIP
        const wKerani = wMandor; // same width as Mandor (per request)
        const wBlok = 16;
        const wTPH = 16;
        const wPotong = 16;
        const wPemanen = wMandor; // same width as Mandor (per request)
        const wTotal = 12;
        const wRupiah = 20;
        const nonMetricSum = wMandor + wKerani + wBlok + wTPH + wPotong + wPemanen + wTotal + wRupiah; // sum of non-metric columns
        const metricWidth = (sharedWidth - nonMetricSum) / 9; // 9 metric columns

        // Generate main table (match buah.html columns)
        autoTable(doc, {
            startY: currentY,
            margin: { left: sharedLeftMargin, right: sharedRightMargin },
            head: [
                [
                    { content: 'Mandor', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Kerani Panen', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Blok', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'No. TPH', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'No. Potong', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Pemanen', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Total JJG', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'MSK', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'MTH', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BST', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BTP', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BRD', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BDT', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BKL', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'PTY', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BBT', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'Rupiah', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                ],
                [
                    'Jjg', 'Jjg', 'Jjg', 'Jjg', 'Kg', 'Jjg', 'Jjg', 'Jjg', 'Jjg'
                ],
            ],
            body: bodyData,
            theme: 'grid',
            styles: {
                fontSize: 7, // increased for better readability
                cellPadding: 0.8,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                halign: 'center',
                valign: 'middle',
                minCellHeight: 3.5,
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                halign: 'center',
                valign: 'middle',
                fontSize: 7, // increased for better readability
                minCellHeight: 3.5,
                lineWidth: 0.1,
            },
            columnStyles: {
                0: { cellWidth: wMandor, halign: 'left' }, // Mandor
                1: { cellWidth: wKerani, halign: 'left' }, // Kerani
                2: { cellWidth: wBlok, halign: 'center' }, // Blok
                3: { cellWidth: wTPH, halign: 'center' }, // No. TPH
                4: { cellWidth: wPotong, halign: 'center' }, // No. Potong
                5: { cellWidth: wPemanen, halign: 'left' }, // Pemanen
                6: { cellWidth: wTotal, halign: 'right' }, // Total JJG
                7: { cellWidth: metricWidth, halign: 'right' }, // MSK
                8: { cellWidth: metricWidth, halign: 'right' }, // MTH
                9: { cellWidth: metricWidth, halign: 'right' }, // BST
                10: { cellWidth: metricWidth, halign: 'right' }, // BTP
                11: { cellWidth: metricWidth, halign: 'right' }, // BRD
                12: { cellWidth: metricWidth, halign: 'right' }, // BDT
                13: { cellWidth: metricWidth, halign: 'right' }, // BKL
                14: { cellWidth: metricWidth, halign: 'right' }, // PTY
                15: { cellWidth: metricWidth, halign: 'right' }, // BBT
                16: { cellWidth: wRupiah, halign: 'right' },
            },
            didParseCell: (data: any) => {
                if (data.section === 'body') {
                    const rowIndex = data.row.index;
                    const row = tableData[rowIndex];
                    if (row?.isSubtotal || row?.isTotal) {
                        data.cell.styles.fontStyle = 'bold';
                    }
                    // Left align textual columns
                    if (data.column.index === 0 || data.column.index === 1 || data.column.index === 5) {
                        data.cell.styles.halign = 'left';
                    }
                    // Remove internal horizontal lines within Mandor and Kerani columns until subtotal
                    if ((data.column.index === 0 || data.column.index === 1) && !row.isSubtotal && !row.isTotal) {
                        const nextRow = tableData[rowIndex + 1];
                        const hasValue = data.cell.raw && data.cell.raw !== '';
                        const nextIsEmptySameGroup = nextRow && !nextRow.isSubtotal && !nextRow.isTotal && (data.column.index === 0 ? nextRow.mandor === '' : nextRow.kerani === '');

                        let topBorder = 0.1;
                        let bottomBorder = 0.1;

                        if (!hasValue) {
                            topBorder = 0;
                        }

                        if (nextIsEmptySameGroup) {
                            bottomBorder = 0;
                        }

                        (data.cell.styles as any).lineWidth = { top: topBorder, right: 0.1, bottom: bottomBorder, left: 0.1 } as any;
                    }
                }
            },
        });

        // Get final Y position after table
        const lastTable = (doc as any).lastAutoTable;
        let finalY = lastTable ? lastTable.finalY : currentY + 100;
        finalY += 2;

        // Add second table - Tabel Keterangan/Inspeksi (dynamic per unique block)
        const formatRate = (rate: number) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(rate);

        const apiPenaltyMap = new Map<string, PenaltyRate>();
        if (penaltyRates) {
            penaltyRates.forEach(pr => apiPenaltyMap.set(pr.code, pr));
        }

        const hardcodedItems = [
            { code: 'MTH', name: 'Buah Mentah (Janjang)' },
            { code: 'BST', name: 'Buah Busuk / Tangkos (Janjang)' },
            { code: 'BTP', name: 'Buah Tangkai Panjang (Janjang)' },
            { code: 'MSK', name: 'Buah Masak (Janjang)' },
            { code: 'BRD', name: 'Brondolan (Kilogram)' },
            { code: 'BDT', name: 'Buah Dimakan Tikus (Janjang)' },
            { code: 'BKL', name: 'Buah < 5 Kg (Janjang)' },
            { code: 'PTY', name: 'Parthenocarpy (Janjang)' },
            { code: 'BBT', name: 'Buah Batu (Janjang)' }
        ];

        const allLines: string[] = [];

        hardcodedItems.forEach(item => {
            if (apiPenaltyMap.has(item.code)) {
                const pr = apiPenaltyMap.get(item.code)!;
                allLines.push(`${pr.code} : ${pr.name} (Rp ${formatRate(pr.rate)})`);
                apiPenaltyMap.delete(item.code);
            } else {
                allLines.push(`${item.code} : ${item.name}`);
            }
        });

        // Add any remaining API codes not in the hardcoded list
        apiPenaltyMap.forEach(pr => {
            allLines.push(`${pr.code} : ${pr.name} (Rp ${formatRate(pr.rate)})`);
        });

        const half = Math.ceil(allLines.length / 2);
        const ketLeftText = allLines.slice(0, half).join('\n');
        const ketRightText = allLines.slice(half).join('\n');

        // Aggregate totals per unique block
        const blockMap = new Map<string, ReturnType<typeof calculateSubtotals>>();
        group.items.forEach(it => {
            const key = it.block || '-';
            if (!blockMap.has(key)) blockMap.set(key, {
                total_qty: 0, ripe_qty: 0, unripe_qty: 0, rotten_qty: 0,
                lstalk_qty: 0, loose_qty: 0, abnormal_01_qty: 0, abnormal_02_qty: 0, abnormal_03_qty: 0, abnormal_04_qty: 0, total_rupiah: 0,
            });
            const agg = blockMap.get(key)!;
            agg.total_qty += it.total_qty || 0;
            agg.ripe_qty += it.ripe_qty || 0;
            agg.unripe_qty += it.unripe_qty || 0;
            agg.rotten_qty += it.rotten_qty || 0;
            agg.lstalk_qty += it.lstalk_qty || 0;
            agg.loose_qty += it.loose_qty || 0;
            agg.abnormal_01_qty += it.abnormal_01_qty || 0;
            agg.abnormal_02_qty += it.abnormal_02_qty || 0;
            agg.abnormal_03_qty += it.abnormal_03_qty || 0;
            agg.abnormal_04_qty += it.abnormal_04_qty || 0;
        });

        const keteranganBody: any[] = [];
        const blocksArr = Array.from(blockMap.entries());
        const pairsCount = (blocksArr.length * 2) + 1; // +1 to include the final Total row
        blocksArr.forEach(([blk, agg], idx) => {
            if (idx === 0) {
                // First block: include keterangan cells with rowSpan to merge until last row
                keteranganBody.push([
                    { content: ketLeftText, rowSpan: pairsCount, styles: { halign: 'left', valign: 'top' } },
                    { content: ketRightText, rowSpan: pairsCount, styles: { halign: 'left', valign: 'top' } },
                    `${blk}`,
                    agg.total_qty || '',
                    agg.ripe_qty || '',
                    agg.unripe_qty || '',
                    agg.rotten_qty || '',
                    agg.lstalk_qty || '',
                    agg.loose_qty ? formatDecimal(agg.loose_qty) : '',
                    agg.abnormal_01_qty || '',
                    agg.abnormal_02_qty || '',
                    agg.abnormal_03_qty || '',
                    agg.abnormal_04_qty || '',
                ]);
                keteranganBody.push([
                    '%',
                    calculatePercentage(agg.total_qty, agg.total_qty),
                    calculatePercentage(agg.ripe_qty, agg.total_qty),
                    calculatePercentage(agg.unripe_qty, agg.total_qty),
                    calculatePercentage(agg.rotten_qty, agg.total_qty),
                    calculatePercentage(agg.lstalk_qty, agg.total_qty),
                    '', // BRD percent blank
                    calculatePercentage(agg.abnormal_01_qty, agg.total_qty),
                    calculatePercentage(agg.abnormal_02_qty, agg.total_qty),
                    calculatePercentage(agg.abnormal_03_qty, agg.total_qty),
                    calculatePercentage(agg.abnormal_04_qty, agg.total_qty),
                ]);
            } else {
                // Subsequent blocks: omit the two keterangan columns (rowSpan covers them)
                keteranganBody.push([
                    `${blk}`,
                    agg.total_qty || '',
                    agg.ripe_qty || '',
                    agg.unripe_qty || '',
                    agg.rotten_qty || '',
                    agg.lstalk_qty || '',
                    agg.loose_qty ? formatDecimal(agg.loose_qty) : '',
                    agg.abnormal_01_qty || '',
                    agg.abnormal_02_qty || '',
                    agg.abnormal_03_qty || '',
                    agg.abnormal_04_qty || '',
                ]);
                keteranganBody.push([
                    '%',
                    calculatePercentage(agg.total_qty, agg.total_qty),
                    calculatePercentage(agg.ripe_qty, agg.total_qty),
                    calculatePercentage(agg.unripe_qty, agg.total_qty),
                    calculatePercentage(agg.rotten_qty, agg.total_qty),
                    calculatePercentage(agg.lstalk_qty, agg.total_qty),
                    '', // BRD percent blank
                    calculatePercentage(agg.abnormal_01_qty, agg.total_qty),
                    calculatePercentage(agg.abnormal_02_qty, agg.total_qty),
                    calculatePercentage(agg.abnormal_03_qty, agg.total_qty),
                    calculatePercentage(agg.abnormal_04_qty, agg.total_qty),
                ]);
            }
        });
        // Final totals row for Keterangan: merge Total + Total JJG, then show sums for metrics
        keteranganBody.push([
            { content: 'Total', colSpan: 2, styles: { halign: 'center', fontStyle: 'bold' } },
            // metrics totals across all blocks (use grandTotal)
            grandTotal.ripe_qty || '',
            grandTotal.unripe_qty || '',
            grandTotal.rotten_qty || '',
            grandTotal.lstalk_qty || '',
            grandTotal.loose_qty ? formatDecimal(grandTotal.loose_qty) : '',
            grandTotal.abnormal_01_qty || '',
            grandTotal.abnormal_02_qty || '',
            grandTotal.abnormal_03_qty || '',
            grandTotal.abnormal_04_qty || '',
        ]);

        autoTable(doc, {
            startY: finalY,
            margin: { left: sharedLeftMargin, right: sharedRightMargin },
            head: [
                [
                    { content: 'Keterangan', colSpan: 2, rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Total', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Total JJG', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'MSK', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'MTH', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BST', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BTP', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BRD', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BDT', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BKL', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'PTY', styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'BBT', styles: { halign: 'center', fontStyle: 'bold' } },
                ],
                [
                    'Jjg', 'Jjg', 'Jjg', 'Jjg', 'Kg', 'Jjg', 'Jjg', 'Jjg', 'Jjg'
                ],
            ],
            body: keteranganBody,
            theme: 'grid',
            styles: {
                fontSize: 7, // increased for better readability
                cellPadding: 0.8,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                minCellHeight: 3.5,
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                halign: 'center',
                valign: 'middle',
                fontSize: 7, // increased for better readability
                minCellHeight: 3.5,
                lineWidth: 0.1,
            },
            columnStyles: (() => {
                const ketCol0 = 35;
                const ketCol1 = 35;
                const ketTotal = 14;
                const ketTotalJJG = 14;
                const ketMetric = (sharedWidth - (ketCol0 + ketCol1 + ketTotal + ketTotalJJG)) / 9;
                return {
                    0: { cellWidth: ketCol0, halign: 'left', cellPadding: { left: 1, right: 0.5, top: 1, bottom: 1 } },
                    1: { cellWidth: ketCol1, halign: 'left', cellPadding: { left: 1, right: 0.5, top: 1, bottom: 1 } },
                    2: { cellWidth: ketTotal, halign: 'center' }, // Total
                    3: { cellWidth: ketTotalJJG, halign: 'right', valign: 'middle' }, // Total JJG
                    4: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    5: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    6: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    7: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    8: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    9: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    10: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    11: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                    12: { cellWidth: ketMetric, halign: 'right', valign: 'middle' },
                } as any;
            })(),
            didDrawCell: (data: any) => {
                // Remove the vertical border between the two Keterangan columns
                if (data.section !== 'body' && data.section !== 'head') return;
                if (data.column.index === 0) {
                    (data.cell.styles as any).lineWidth = { top: 0.1, right: 0, bottom: 0.1, left: 0.1 } as any;
                }
                if (data.column.index === 1) {
                    (data.cell.styles as any).lineWidth = { top: 0.1, right: 0.1, bottom: 0.1, left: 0 } as any;
                }
            },
        });

        // Get final Y position after second table
        const lastTable2 = (doc as any).lastAutoTable;
        finalY = lastTable2 ? lastTable2.finalY : finalY + 50;
        finalY += 3;

        // Add Kesimpulan/Saran table
        autoTable(doc, {
            startY: finalY,
            margin: { left: sharedLeftMargin, right: sharedRightMargin },
            head: [[
                {
                    content: 'Kesimpulan/Saran',
                    styles: {
                        halign: 'center' as const,
                        fontStyle: 'bold' as const,
                        fontSize: 7,
                    }
                }
            ]],
            body: [['']],
            theme: 'grid',
            styles: {
                fontSize: 6, // increased for better readability
                cellPadding: 0.8,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                minCellHeight: 20, // increased height per request
                valign: 'top' as const,
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold' as const,
                halign: 'center' as const,
                fontSize: 7, // increased for better readability
                lineWidth: 0.1,
                minCellHeight: 3.2,
            },
            columnStyles: {
                0: { cellWidth: sharedWidth },
            },
        });

        // Signature section (like Mutu Ancak)
        const kesimpulanFinalY = (doc as any).lastAutoTable.finalY + 6;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');

        // Signature positions optimized for A4 paper with better margins
        const signatureY = kesimpulanFinalY;
        const signatureSpacing = 16;
        const sig1X = margins.left + 35;
        const sig2X = pageWidth / 2;
        const sig3X = pageWidth - margins.right - 35;

        const lineHeight2 = 4;
        const signatureLineY = signatureY + (lineHeight2 * 3.5);
        const dateY = signatureLineY + lineHeight2;

        // Mandor signature section (left)
        doc.setFont('helvetica', 'bold');
        // --- Add signature section (Boxed Style) ---
        doc.setFontSize(7);

        const sigY = kesimpulanFinalY;
        const boxWidth = 45;          // Lebar kotak per orang
        const headerHeight = 5;       // Tinggi bagian atas (Yang Mengawasi)
        const middleHeight = 20;      // Tinggi bagian tengah (Nama/TTD)
        const footerHeight = 5;       // Tinggi bagian bawah (Tanggal)
        const totalBoxHeight = headerHeight + middleHeight + footerHeight;

        const jobLvl = group.job_level ? group.job_level.charAt(0).toUpperCase() : '';
        let signatures: any[] = [];

        if (jobLvl === 'C') {
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' }
            ];
        } else if (jobLvl === 'D') {
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' },
                { title: 'Diketahui oleh,', name: group.manager || '(______________)', role: 'manager' }
            ];
        } else if (jobLvl === 'E') {
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' },
                { title: 'Diketahui oleh,', name: group.askep || '(______________)', role: 'askep' },
                { title: 'Diketahui oleh,', name: group.manager || '(______________)', role: 'manager' }
            ];
        } else {
            // Else: displays 4 boxes (Supervisor, Asisten, Askep, Manager)
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' },
                { title: 'Diketahui oleh,', name: group.asisten || '(______________)', role: 'asisten' },
                { title: 'Diketahui oleh,', name: group.askep || '(______________)', role: 'askep' },
                { title: 'Diketahui oleh,', name: group.manager || '(______________)', role: 'manager' }
            ];
        }

        doc.setLineWidth(0.1);
        // Create boxes
        signatures.forEach((sig, index) => {
            const x = sharedLeftMargin + (boxWidth * index);

            // outer box
            doc.rect(x, sigY, boxWidth, totalBoxHeight);

            // horizontal lines to divide header/middle/footer
            doc.line(x, sigY + headerHeight, x + boxWidth, sigY + headerHeight);

            // horizontal line to divide middle/footer
            doc.line(x, sigY + headerHeight + middleHeight, x + boxWidth, sigY + headerHeight + middleHeight);



            // Header: Judul (Bold)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(6.5);
            doc.text(sig.title, x + (boxWidth / 2), sigY + 3.5, { align: 'center' });

            // Middle: Nama
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);

            const rawName = sig.name || '(______________)';
            // prevent overlapping text by splitting into multiple lines if too long for the box
            const splitName = doc.splitTextToSize(rawName, boxWidth - 4);


            const lineSpacing = 3;
            const bottomY = sigY + headerHeight + middleHeight - 1;
            const startYName = bottomY - ((splitName.length - 1) * lineSpacing);

            // print name
            doc.text(splitName, x + (boxWidth / 2), startYName, { align: 'center' });

            // Footer: Date
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.text('Tanggal :', x + 2, sigY + headerHeight + middleHeight + 3.5);
        });
    });

    return doc;
};

/**
 * Download PDF file
 */
export const downloadMutuBuahPDF = (
    data: MutuBuahData[],
    inspectionDate: string,
    penaltyRates?: PenaltyRate[]
) => {
    const groupedData = groupDataByMandor(data);
    const doc = generateMutuBuahPDF(groupedData, inspectionDate, penaltyRates);

    const filename = `Mutu_Buah_${format(new Date(inspectionDate), 'yyyy-MM-dd')}.pdf`;
    doc.save(filename);
    trackDownload('Form Mutu Buah', { filename });
};
